import { useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck } from "lucide-react";
import AuthLayout from "@/components/AuthLayout";
import { api, formatApiError } from "@/lib/api";

const inputCls = "w-full bg-elevated border hairline-gold px-4 py-3.5 text-sm text-neutral-200 placeholder:text-neutral-600 focus:outline-none focus:border-amber-500/50 transition-colors";

export default function SignIn() {
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", form);
      setUser(data);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    await api.post("/auth/logout");
    setUser(null);
  };

  return (
    <AuthLayout eyebrow="Members" title={<>Sign <span className="italic gold-text">in</span></>}>
      {user ? (
        <div data-testid="signin-welcome-panel" className="glass-panel p-8 sm:p-10 text-center">
          <ShieldCheck className="mx-auto text-gold mb-5" size={36} strokeWidth={1.25} />
          <h2 className="font-serif text-2xl text-amber-100/95 mb-2">Welcome back, {user.display_name}</h2>
          <p className="text-sm text-neutral-400 mb-1 capitalize">{user.role} account</p>
          <p className="text-xs uppercase tracking-[0.2em] text-gold/80 mb-8">Status: {(user.status || "").replace(/_/g, " ")}</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/" data-testid="signin-home-link" className="px-8 py-3 bg-gold text-obsidian text-xs uppercase tracking-[0.2em] font-medium hover:bg-gold-bright transition-colors">
              Enter the Society
            </Link>
            <button onClick={logout} data-testid="signin-logout-button" className="px-8 py-3 border hairline-gold text-xs uppercase tracking-[0.2em] text-neutral-300 hover:border-amber-400/60 transition-colors">
              Sign Out
            </button>
          </div>
        </div>
      ) : (
        <form data-testid="signin-form" onSubmit={submit} className="glass-panel p-8 sm:p-10 space-y-5">
          <div>
            <label className="block text-xs uppercase tracking-[0.2em] text-neutral-400 mb-2">Email Address</label>
            <input data-testid="signin-email-input" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@example.com" className={inputCls} />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-[0.2em] text-neutral-400 mb-2">Password</label>
            <input data-testid="signin-password-input" type="password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Your password" className={inputCls} />
          </div>
          {error && <p data-testid="signin-error" className="text-sm text-red-400">{error}</p>}
          <button
            type="submit"
            data-testid="signin-submit-button"
            disabled={loading}
            className="w-full py-4 bg-gold text-obsidian text-sm uppercase tracking-[0.25em] font-medium hover:bg-gold-bright transition-all duration-300 disabled:opacity-50"
          >
            {loading ? "Signing In…" : "Sign In"}
          </button>
          <p className="text-sm text-neutral-500 text-center pt-2">
            New to the Society?{" "}
            <Link to="/join/provider" data-testid="signin-join-provider-link" className="text-gold hover:text-gold-bright transition-colors">Advertise</Link>
            {" · "}
            <Link to="/join/client" data-testid="signin-join-client-link" className="text-gold hover:text-gold-bright transition-colors">Join as Client</Link>
          </p>
        </form>
      )}
    </AuthLayout>
  );
}
